import {
  DefinedInitialDataOptions,
  QueryKey,
  UndefinedInitialDataOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query"

import { ApiError, ApiResponse } from "@/app/api/type"
import { createAccountStore } from "@/stores/account"

const useMutationHook = <TData, TError, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  invalidateQueryKey?: QueryKey,
  options?: UseMutationOptions<TData, TError, TVariables>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => {
      // 요청 성공시 캐시 무효화
      if (invalidateQueryKey) {
        invalidateQueryKey.forEach((key) => {
          queryClient.invalidateQueries({
            queryKey: Array.isArray(key) ? key : [key],
          })
        })
      }
    },

    ...options,
  })
}

const useQueryHook = <TData, TVariables>(data: {
  queryKey: QueryKey
  queryFn: (variables: TVariables) => Promise<ApiResponse<TData>>
  // queryKey/queryFn 은 이 훅이 채우므로 나머지 옵션만 부분 전달 (기존: 전체 필수형이라 {enabled} 등 부분 전달이 전부 타입 에러)
  options?: Omit<
    Partial<
      | UseQueryOptions<TData, ApiError, TData>
      | DefinedInitialDataOptions<TData, ApiError, TData>
      | UndefinedInitialDataOptions<TData, ApiError, TData>
    >,
    "queryKey" | "queryFn"
  >
}) => {
  const { queryKey, queryFn, options } = data
  const { selectedIdfTeam } = createAccountStore((state) => ({
    selectedIdfTeam: state.selectedIdfTeam,
  }))

  return useQuery({
    queryKey: [...queryKey, selectedIdfTeam],
    queryFn: async () => {
      const res = await queryFn({} as TVariables)

      return res.response as TData
    },
    ...options,
    enabled:
      // 유저정보를 가져오는 api 호출이 있어야 다른 api 호출이 가능하도록 설정
      // 단, 공개 API(countryList 등)는 항상 enabled
      (queryKey.includes("readAccountInfo") ||
        queryKey.includes("countryList") ||
        !!selectedIdfTeam) &&
      (options?.enabled !== undefined ? options?.enabled : true),
  })
}

export { useMutationHook, useQueryHook }
